import type {
  CardElementError,
  CardElementEvent,
  CardElementRef,
  OnBinLookupResult,
} from '@flywire/internal-react-elements';
import {
  trackBinLookup,
  trackCardElementEventForLegacySurcharge,
  trackCardTokenizationSuccessForCardSurchargePayment,
  trackChangePaymentMethodInGBPChargeError,
  trackChargeResponse,
} from 'actions/tracker/tracker';
import {
  setDisplayPayerInformation,
  setPayerInformationCountrySuggestion,
  setShouldDisablePayerInformationFields,
} from 'actions/ui/ui';
import { CardForm } from 'components/CardForm/CardForm';
import { ErrorComponent as CheckoutErrorComponent } from 'components/Error/Error';
import { PausedError } from 'components/PausedError/PausedError';
import {
  Captcha,
  type CaptchaRef,
} from 'components/thirdParty/Captcha/Captcha';
import { Heading } from 'components/ui/Heading/Heading';
import { Spinner } from 'components/ui/Spinner/Spinner';
import { PAUSED_ERROR } from 'constants/errors';
import type { HttpError } from 'constants/http';
import { useCreateCheckoutSession } from 'hooks/useCreateCheckoutSession/useCreateCheckoutSession';
import { useExperimentTrack } from 'hooks/useExperiments/useExperimentTrack';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAccessToken } from 'selectors/entities/authentication/authentication';
import { getOrder, getSenderEmail } from 'selectors/entities/order/order';
import {
  getSelectedOffer,
  hasGatewayOrigin,
  shouldDisplayPayerInformation,
} from 'selectors/ui/ui';
import { cardOnFile } from 'services/cardOnFile/cardOnFile';
import { useDispatch } from 'store/configureStore';
import { useTranslations } from 'utils';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { isHttpErrorWithParsedBody } from 'utils/errors/errors';
import { Redirection } from './Redirection';
import { Success } from './Success';
import { Surcharge } from './Surcharge';
import { SurchargeError as ErrorComponent } from './SurchargeError';
import { type ChargeError, useCharge } from './useCharge';
import './CreditCardPaymentForm.scss';

const EXPERIMENT_EVENT_NAME = 'surcharge_payment_created';
type CardDetails = {
  cardClassification: string;
  country: string;
  productType: string;
};
type SuccessResponse = {
  confirm: {
    method: string;
    url: string;
  };
};
type Decision = {
  action: 'charge' | 'redirection' | 'surcharge';
  percentage?: number;
};

type CardFormProps = {
  goToStep: (step: string) => void;
};

type ApiError = {
  type: string;
  parsedBody?: { errors?: { type: string; message: string }[] };
  status: number;
};
export const CreditCardPaymentForm = ({ goToStep }: CardFormProps) => {
  const dispatch = useDispatch();
  const i18n = useTranslations();
  const captchaRef = useRef<CaptchaRef>(null);
  const trackExperimentEvent = useExperimentTrack();

  const [isLoading, setIsLoading] = useState(true);
  const [cardDetails, setCardDetails] = useState<CardDetails>({
    cardClassification: '',
    country: '',
    productType: '',
  });
  const [showSurchargeMessage, setShowSurchargeMessage] = useState(false);
  const [showRedirectionMessage, setShowRedirectionMessage] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [decision, setDecision] = useState<Decision | undefined>();
  const [showErrorComponent, setShowErrorComponent] = useState(false);
  const [paymentId, setPaymentId] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<string>('');
  const [errorStatus, setErrorStatus] = useState<ChargeError | undefined>();
  const [binLookupRequest, setBinLookupRequest] = useState<
    undefined | 'started' | 'finished'
  >();
  const [showPausedError, setShowPausedError] = useState(false);
  const ref = useRef<CardElementRef>(null);

  const accessToken = useSelector(getAccessToken);
  const { id, token } = useSelector(getOrder);
  const displayPayerInfo = useSelector(shouldDisplayPayerInformation);
  const {
    price: { currency },
  } = useSelector(getSelectedOffer);
  const senderEmail = useSelector(getSenderEmail);

  const { isLoading: isCharging, isSuccess, mutateAsync: charge } = useCharge();

  const {
    mutate: createSession,
    sessionId,
    error: createSessionError,
    isPending: isCreatingSession,
  } = useCreateCheckoutSession({
    sessionData: {
      sessionType: 'card_tokenization_for_one_off',
    },
  });
  const isFromGateway = useSelector(hasGatewayOrigin);

  const onBinLookup = async (response: OnBinLookupResult) => {
    if (response.status === 'success') {
      dispatch(trackBinLookup(response));
    } else {
      dispatch(trackBinLookup(response));
      setDecision({ action: 'charge' });
      if (response.status === 'failed') {
        errorNotifier.notify(
          new Error('Bin Lookup service failure', { cause: response.result.e }),
        );
      }
      setBinLookupRequest('finished');
      return;
    }

    const { result } = response;
    setCardDetails(result);

    try {
      const decision = await cardOnFile.chargeDecision({
        cardClassification: result.cardClassification,
        cardCountry: result.country,
        cardProductType: result.productType,
        orderId: id,
        orderToken: token,
      });

      setDecision(decision);
    } catch (error) {
      errorNotifier.notify(error);
      setDecision({ action: 'charge' });
    } finally {
      setBinLookupRequest('finished');
    }
  };

  const submit = async () => await ref.current?.submit();

  const resetCaptcha = () => {
    captchaRef.current?.reset();
  };

  const handleFormSubmit = async () => {
    if (!isFormValid) {
      submit();
      return;
    }

    switch (decision?.action) {
      case 'redirection':
        setShowRedirectionMessage(true);
        break;
      case 'surcharge':
        setShowSurchargeMessage(true);
        break;
      case 'charge':
        submit();
        break;
      default:
    }
  };

  const onSubmit = async () => {
    if (captchaRef.current?.isEnabled()) {
      captchaRef.current?.execute();
    } else {
      handleFormSubmit();
    }
  };

  const verifyCaptcha = async (token: string) => {
    if (token) {
      handleFormSubmit();
    } else {
      setShowErrorComponent(true);
    }
  };

  const onEvent = (event: CardElementEvent) => {
    dispatch(
      trackCardElementEventForLegacySurcharge({
        type: event.type,
        content: event,
        sessionId,
      }),
    );
    if (event.type === 'form_changed') {
      setIsFormValid(event.extra.isValid);
    } else if (event.type === 'bin_lookup_started') {
      setBinLookupRequest('started');
    }
  };

  const onSuccess = async (result: SuccessResponse) => {
    const captchaResponse = captchaRef.current?.getToken();
    const {
      confirm: { url: confirmURL },
    } = result;

    dispatch(
      trackCardTokenizationSuccessForCardSurchargePayment({
        productType: cardDetails.productType,
      }),
    );

    try {
      const response = await charge({
        accessToken,
        captchaResponse: captchaResponse as string,
        cardClassification: cardDetails.cardClassification,
        cardCountry: cardDetails.country,
        cardProductType: cardDetails.productType,
        confirmURL,
        orderId: id,
        orderToken: token as string,
        sessionId: sessionId as string,
        isFromGateway,
      });
      setPaymentId(response.paymentId);
      setPaymentStatus(response.status);
      trackExperimentEvent({
        eventName: EXPERIMENT_EVENT_NAME,
        userEmail: senderEmail as string,
        eventProperties: {
          decision: decision?.action || 'charge',
        },
      });

      dispatch(trackChargeResponse(response));
    } catch (error) {
      createSession({});
      let accessPausedError: ApiError | undefined;

      if (isHttpErrorWithParsedBody(error)) {
        const httpError = error as HttpError;
        const { errors = [] } = httpError.parsedBody ?? {};

        accessPausedError = (errors as ApiError[]).find(
          error => error.type === PAUSED_ERROR,
        );

        if (accessPausedError) {
          setShowPausedError(true);
        }

        setErrorStatus((error as HttpError).parsedBody?.status as ChargeError);
      }

      if (!accessPausedError) {
        setShowErrorComponent(true);
      }
    }

    resetCaptcha();
  };

  const handleAcceptSurcharge = async () => {
    setShowSurchargeMessage(false);

    submit();
  };

  const handleChangeCountry = () => {
    setShowRedirectionMessage(false);
    setShowSurchargeMessage(false);
    dispatch(
      setPayerInformationCountrySuggestion(
        i18n.t('surcharge.change.country.or.paymentOption'),
      ),
    );
    if (!displayPayerInfo) {
      const payerInformationEditableFields = [
        'sender_country',
        'sender_city',
        'sender_address1',
      ];
      dispatch(
        setShouldDisablePayerInformationFields({
          disablePayerInformationFields: true,
          payerInformationEditableFields,
        }),
      );
      dispatch(setDisplayPayerInformation(true));
    }
    goToStep('paymentInformation');
  };

  const onErrorRetry = async () => {
    try {
      await createSession({});
      setShowErrorComponent(false);
      setErrorStatus(undefined);
    } catch (e) {
      errorNotifier.notify(e);
    }
    resetCaptcha();
  };

  const onChangePaymentMethod = () => {
    dispatch(
      trackChangePaymentMethodInGBPChargeError({
        productType: cardDetails.productType,
      }),
    );
    goToStep('paymentOptions');
  };

  const renderCardElementContent = () => {
    if (isCreatingSession) {
      return (
        <Spinner data-testid="surchargeFormSpinner" className="h-[50vh]" />
      );
    }

    if (createSessionError) {
      return (
        <CheckoutErrorComponent errorKey="surcharge.card.createSessionError" />
      );
    }

    if (showSurchargeMessage) {
      return (
        <Surcharge
          feeRate={decision?.percentage || 0}
          onAcceptFee={handleAcceptSurcharge}
          onChangeCountry={handleChangeCountry}
          productType={cardDetails.productType}
        />
      );
    }

    if (showRedirectionMessage) {
      return (
        <Redirection
          onChangeCard={() => setShowRedirectionMessage(false)}
          onChangeCountry={handleChangeCountry}
          productType={cardDetails.productType}
        />
      );
    }

    if (showErrorComponent) {
      return (
        <ErrorComponent
          status={errorStatus}
          onRetry={onErrorRetry}
          onChangePaymentMethod={onChangePaymentMethod}
        />
      );
    }

    if (isSuccess) {
      return <Success paymentId={paymentId} status={paymentStatus} />;
    }

    return (
      <div className="CreditCardPaymentForm">
        <div className="CreditCardPaymentForm-content">
          <Heading as="h2" size="large" className="fontWeight-semiBold">
            {i18n.t('cardForm.title')}
          </Heading>
          <>
            {isLoading || isCharging ? (
              <Spinner data-testid="surchargeFormSpinner" />
            ) : (
              <>
                <CardForm.Form
                  onSubmit={onSubmit}
                  buttonLabel={i18n.t('cardForm.pay') as string}
                  buttonDisabled={binLookupRequest === 'started'}
                  isLoading={binLookupRequest === 'started'}
                />
                <div className="CreditCardPaymentForm-disclaimer" role="alert">
                  {i18n.t('cardForm.disclaimer')}
                </div>
              </>
            )}
          </>
        </div>
      </div>
    );
  };

  const onError = (e: CardElementError) => {
    if (e.type !== 'invalid_card_info') {
      setShowErrorComponent(true);
    }
    resetCaptcha();
  };

  return (
    <>
      <Captcha
        ref={captchaRef}
        onVerify={verifyCaptcha}
        onError={() => setShowErrorComponent(true)}
        data-testid="captcha"
      />
      {showPausedError ? (
        <PausedError />
      ) : (
        <CardForm
          cardFormRef={ref}
          sessionId={sessionId as string}
          onSuccess={onSuccess}
          onBinLookup={onBinLookup}
          onError={onError}
          onEvent={onEvent}
          onStartLoading={() => setIsLoading(true)}
          onEndLoading={() => setIsLoading(false)}
          currency={currency as string}
        >
          {renderCardElementContent()}
        </CardForm>
      )}
    </>
  );
};
